import type { DragEndEvent } from '@dnd-kit/core';
import { DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type {
  EmailVerification,
  IMailrailDripStep,
  MailrailDripEmailCampaign,
  Template,
} from '@feathr/blackbox';
import {
  EPinpointRequestStatus,
  MailrailDripStep,
  MAX_MAILRAIL_DRIP_CAMPAIGN_STEPS,
  triggerableMailingConstraints,
} from '@feathr/blackbox';
import {
  Button,
  ButtonValid,
  CardV2 as Card,
  Fieldset,
  InlineEdit,
  Input,
  Layout,
  StepSection,
} from '@feathr/components';
import { flattenErrors } from '@feathr/hooks';
import type { ListResponse } from '@feathr/rachis';

import type { ICampaignValidationErrors } from '../../../CampaignSummary';
import MailrailTestSendForm from '../MailrailTestSendForm';
import DripStepCard from './DripStepCard';
import DripStepCardPreview from './DripStepCardPreview';
import EnrollmentCardEditor from './EnrollmentCardEditor';
import { getStepLockState } from './MailrailCampaignStepDripBuilder.hooks';

/** Embedded steps allowed in addition to the campaign-as-step-0. */
const MAX_EMBEDDED_STEPS = MAX_MAILRAIL_DRIP_CAMPAIGN_STEPS - 1;

// The subset of the campaign's fields configured in the Step 0 / initial enrollment card. Validation
// errors for these fields flag the Step 0 card as invalid.
const CAMPAIGN_VALIDATION_FIELDS = [
  'enrollment_step_name',
  'predicates',
  'subtype',
  ...Object.keys(triggerableMailingConstraints),
];

/** The subset of an EmailVerifications `ListResponse` that drip validation reads. */
interface IDripSenderVerifications {
  readonly models: readonly EmailVerification[];
  readonly isPending: boolean;
}

export interface IValidateStepDripBuilderResult {
  /** Grouped validation errors per card, index-aligned (0 = campaign, 1..N = steps). */
  stepErrors: ICampaignValidationErrors[];
  /**
   * Every invalid card's index (0-based, card-aligned) — a card with field errors, an unusable
   * sender, or a template problem. Drives each card's StepSection complete/incomplete header icon,
   * and (via its first entry) which card auto-opens. A card can be invalid via its sender alone
   * with no entry in `stepErrors`, so completeness can't be derived from `stepErrors`.
   */
  invalidCardIndexes: number[];
  /** Publish-gating aggregate: campaign-name + step-count + per-invalid-card + per-unverified-sender messages. */
  dripStepsErrors: string[];
  /** The campaign-name field errors, surfaced separately for the Campaign Information card's input. */
  nameErrors: string[];
}

/**
 * Validates a Mailrail drip card-by-card: card 0 is the campaign document, cards 1..N are the
 * embedded steps. A card is invalid if it has missing required fields, an unverified/pending sender,
 * an ephemeral template, or a template with no subject.
 *
 * @param campaign - The drip campaign being edited.
 * @param emailVerifications - The verification list spanning the campaign + step senders. Senders are
 *   only flagged once it has resolved (`isPending` false); while pending we stay optimistic.
 * @param templates - The resolved templates across every card (campaign + steps), for the
 *   saved/subject checks.
 */
export function validateStepDripBuilder(
  campaign: MailrailDripEmailCampaign,
  emailVerifications: IDripSenderVerifications | undefined,
  templates: readonly Template[] | undefined,
): IValidateStepDripBuilderResult {
  const steps = campaign.get('steps');

  const isPending = emailVerifications?.isPending ?? true;
  // Email addresses are case-insensitive, but verification records may differ in case from the
  // address stored on the campaign/step, so normalize both sides before matching.
  const statusByEmail = new Map(
    (emailVerifications?.models ?? []).map((model) => [
      model.get('email')?.toLowerCase(),
      model.get('status'),
    ]),
  );
  const isUnusableSender = (fromAddress?: string): boolean =>
    !isPending &&
    !!fromAddress &&
    statusByEmail.get(fromAddress.toLowerCase()) !== EPinpointRequestStatus.Success;
  const senderMessage = (fromAddress: string): string =>
    statusByEmail.get(fromAddress.toLowerCase()) === EPinpointRequestStatus.Pending
      ? `${fromAddress} is still pending verification`
      : `${fromAddress} is not verified`;

  const templatesById = new Map((templates ?? []).map((template) => [template.id, template]));
  const templateErrorsFor = (templateId?: string): { template: string[]; subject: string[] } => {
    const errors = { template: [] as string[], subject: [] as string[] };
    const template = templateId ? templatesById.get(templateId) : undefined;
    if (!template || template.isPending) {
      return errors;
    }
    if (template.isEphemeral) {
      errors.template.push('The template needs to be saved.');
    }
    if (!template.get('subject')) {
      errors.subject.push('The email must have a subject.');
    }
    return errors;
  };

  const campaignErrors = campaign.validate(['name', 'steps'], false, 'grouped').errors;
  const nameErrors = flattenErrors({ name: campaignErrors.name });
  const stepCountErrors = flattenErrors({ steps: campaignErrors.steps });

  const stepErrors: ICampaignValidationErrors[] = [];
  const invalidCardIndexes: number[] = [];

  const pushCard = (
    baseErrors: ICampaignValidationErrors,
    templateId?: string,
    fromAddress?: string,
  ): void => {
    const { template: templateErrs, subject: subjectErrs } = templateErrorsFor(templateId);
    const errors =
      templateErrs.length || subjectErrs.length
        ? {
            ...baseErrors,
            ...(templateErrs.length
              ? { template: [...(baseErrors.template ?? []), ...templateErrs] }
              : {}),
            ...(subjectErrs.length
              ? { subject: [...(baseErrors.subject ?? []), ...subjectErrs] }
              : {}),
          }
        : baseErrors;
    stepErrors.push(errors);
    if (flattenErrors(errors).length || isUnusableSender(fromAddress)) {
      invalidCardIndexes.push(stepErrors.length - 1);
    }
  };

  // Card 0: the campaign document.
  pushCard(
    campaign.validate(CAMPAIGN_VALIDATION_FIELDS, false, 'grouped').errors,
    campaign.get('template_id'),
    campaign.get('from_address'),
  );

  // Cards 1..N: the embedded steps, each validated through a real ephemeral model.
  (steps ?? []).forEach((step) => {
    pushCard(
      new MailrailDripStep(step).validate([], false, 'grouped').errors,
      step.template_id,
      step.from_address,
    );
  });

  const cardErrors = invalidCardIndexes.map(
    (cardIndex) => `Step ${cardIndex + 1} must be completed`,
  );
  // Per-unusable-sender messages, deduped across cards (a sender shared by the campaign + a step
  // surfaces once).
  const senderMessages = isPending
    ? []
    : [
        ...new Set(
          [campaign.get('from_address'), ...(steps ?? []).map((step) => step.from_address)].filter(
            (address): address is string => !!address,
          ),
        ),
      ]
        .filter((address) => isUnusableSender(address))
        .map((address) => senderMessage(address));

  return {
    stepErrors,
    invalidCardIndexes,
    // Campaign-name and step-count errors gate publish but never flag/auto-expand a card (name
    // belongs to the Campaign Information card; the step count isn't tied to any single card), so
    // they are appended to the aggregate, not the card-index list.
    dripStepsErrors: [...nameErrors, ...stepCountErrors, ...cardErrors, ...senderMessages],
    nameErrors,
  };
}

interface IMailrailCampaignStepDripBuilderProps {
  campaign: MailrailDripEmailCampaign;
  disabled?: boolean;
  /** Verification list spanning the campaign + step senders, fetched once by the coordinator. */
  emailVerifications?: ListResponse<EmailVerification>;
  /** Resolved templates across every card (campaign + steps), for the saved/subject checks. */
  templates?: readonly Template[];
}

/**
 * The Mailrail drip campaign step builder. It renders card 0 (the campaign acting as the
 * enrollment step) followed by one card per embedded step. Editing, adding, removing, and
 * reordering steps mutate ONLY the in-memory campaign model via `campaign.set({ steps })` — the
 * wizard's Save-draft / Publish buttons own persistence, so this coordinator never calls
 * `save`, `patchDirty`, or `setAttributeClean`.
 *
 * @param props - {@link IMailrailCampaignStepDripBuilderProps}.
 */
function MailrailCampaignStepDripBuilder({
  campaign,
  disabled = false,
  emailVerifications,
  templates,
}: Readonly<IMailrailCampaignStepDripBuilderProps>): JSX.Element {
  const { t } = useTranslation();
  const steps = campaign.get('steps') ?? [];

  const { stepErrors, invalidCardIndexes, nameErrors } = validateStepDripBuilder(
    campaign,
    emailVerifications,
    templates,
  );
  const firstInvalidCardIndex = invalidCardIndexes[0];

  const [editingStep, setEditingStep] = React.useState<number | null>(null);
  const [isReordering, setIsReordering] = React.useState(false);
  const [enrollmentTestOpened, setEnrollmentTestOpened] = React.useState(false);

  // The campaign's shadow copy of `steps` holds them as they were last loaded or saved, so it tells
  // getStepLockState which steps are persisted (pinned once the drip has run) versus added since.
  // It lives on the model, so it outlasts remounts as the user moves between wizard steps.
  const originalSteps: IMailrailDripStep[] = campaign.shadowAttributes.steps ?? steps;
  const locks = getStepLockState(
    campaign.get('state'),
    new Set(originalSteps.map((step) => step.step_id)),
    steps.map((step) => step.step_id),
  );

  const pointerSensor = useSensor(PointerSensor, { activationConstraint: { distance: 5 } });
  const sensors = useSensors(pointerSensor);

  function applyStepPatch(index: number, patch: Partial<IMailrailDripStep>): void {
    const next = steps.map((s, i) => (i === index ? { ...s, ...patch } : s));
    campaign.set({ steps: next });
  }

  function handleStopEditing(): void {
    setEditingStep(null);
  }

  function handleEdit(cardIndex: number): void {
    setEditingStep(cardIndex);
  }

  function handleOpenEnrollmentTest(): void {
    setEnrollmentTestOpened(true);
  }

  function handleCloseEnrollmentTest(): void {
    setEnrollmentTestOpened(false);
  }

  function handleDelete(index: number): void {
    campaign.set({ steps: steps.filter((_, i) => i !== index) });
    setEditingStep(null);
  }

  function handleRename(index: number, name?: string): void {
    const updatedSteps = steps.map((step, i) =>
      i === index ? { ...step, name: name ?? '' } : step,
    );
    campaign.set({ steps: updatedSteps });
  }

  function handleRenameEnrollmentStep(value?: string): void {
    campaign.set({ enrollment_step_name: (value || '').trim() });
  }

  function handleAdd(): void {
    // The appended step lands at array index `steps.length`, i.e. card `steps.length + 1` (card 0 is
    // the enrollment step). Open it straight into edit mode so the user can fill it in immediately.
    const newCardIndex = steps.length + 1;
    campaign.set({ steps: [...steps, campaign.getPlaceholderStep()] });
    setEditingStep(newCardIndex);
  }

  function handleDragEnd(event: DragEndEvent): void {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }
    const ids = steps.map((step) => step.step_id);
    const fromIndex = ids.indexOf(active.id as string);
    const toIndex = ids.indexOf(over.id as string);
    if (fromIndex === -1 || toIndex === -1) {
      return;
    }
    // Only draggable steps may move at all.
    if (!locks[fromIndex]?.draggable) {
      return;
    }
    // A moved step may not be dropped above the last pinned (non-draggable) step. Every embedded
    // step is draggable in draft/scheduled state, so lastPinnedIndex is -1 and there is no
    // restriction.
    const lastPinnedIndex = locks.reduce(
      (last, lock, index) => (lock.draggable ? last : index),
      -1,
    );
    if (toIndex <= lastPinnedIndex) {
      return;
    }
    campaign.set({ steps: arrayMove(steps, fromIndex, toIndex) });
  }

  const stepIds = steps.map((step) => step.step_id);

  const addStepErrors: string[] = [];
  if (editingStep !== null) {
    addStepErrors.push(t("New steps can't be added while editing"));
  }
  if (isReordering) {
    addStepErrors.push(t('Finish reordering to add a step'));
  }
  if (steps.length >= MAX_EMBEDDED_STEPS) {
    addStepErrors.push(
      t('A maximum of {{ numSteps }} steps can be added', {
        numSteps: MAX_MAILRAIL_DRIP_CAMPAIGN_STEPS,
      }),
    );
  }

  // The Reorder button is only disabled when NOT already reordering (so the user can always exit
  // via "Done reordering"): either while editing a step, or when there are fewer than 2 steps.
  const reorderStepErrors: string[] = [];
  if (!isReordering && editingStep !== null) {
    reorderStepErrors.push(t('Finish editing to reorder steps'));
  } else if (!isReordering && steps.length < 2) {
    reorderStepErrors.push(t('Add a second step to enable reordering'));
  }

  return (
    <Layout>
      <Card width={'full'}>
        <Card.Header title={t('Campaign Information')} />
        <Card.Content>
          <Fieldset>
            <Input
              attribute={'name'}
              disabled={disabled}
              label={t('Campaign name')}
              model={campaign}
              name={'campaign_name'}
              required={true}
              type={'text'}
              validationError={nameErrors}
            />
          </Fieldset>
        </Card.Content>
      </Card>
      <Card width={'full'}>
        <Card.Header
          description={t(
            'Design the steps of your drip campaign. Each step has an email and automation rules that determine when and how that email should be sent.',
          )}
          title={t('Steps')}
        />
        <Card.Content>
          <StepSection
            collapsed={isReordering}
            defaultOpen={firstInvalidCardIndex === 0}
            disabled={disabled}
            editing={editingStep === 0}
            isComplete={!invalidCardIndexes.includes(0) && editingStep !== 0}
            name={'step-1'}
            stepIndex={0}
            stepNumber={1}
          >
            <StepSection.ReorderableHeader draggable={false} reordering={isReordering}>
              <InlineEdit
                disabled={disabled || isReordering}
                onChange={handleRenameEnrollmentStep}
                validationError={
                  campaign.get('enrollment_step_name') === ''
                    ? t("Step name can't be blank")
                    : undefined
                }
                value={campaign.get('enrollment_step_name')}
              />
            </StepSection.ReorderableHeader>
            <StepSection.Content>
              {editingStep === 0 ? (
                <EnrollmentCardEditor
                  campaign={campaign}
                  disabled={disabled}
                  onDone={handleStopEditing}
                  validationErrors={stepErrors[0] ?? {}}
                />
              ) : (
                <Card theme={'disabled'} width={'full'}>
                  <DripStepCardPreview campaign={campaign} mailing={campaign} />
                  <Card.Actions>
                    <Button
                      disabled={disabled || editingStep !== null || isReordering}
                      name={'step-1-edit'}
                      onClick={handleEdit.bind(null, 0)}
                    >
                      {t('Edit step')}
                    </Button>
                    <Button
                      disabled={disabled || editingStep !== null || isReordering}
                      name={'step-1-test'}
                      onClick={handleOpenEnrollmentTest}
                      type={'secondary'}
                    >
                      {t('Send test email')}
                    </Button>
                  </Card.Actions>
                  <MailrailTestSendForm
                    campaign={campaign}
                    mailing={campaign}
                    onClose={handleCloseEnrollmentTest}
                    opened={enrollmentTestOpened}
                    variant={'modal'}
                  />
                </Card>
              )}
            </StepSection.Content>
          </StepSection>

          {/* Portal dnd-kit's accessibility live region to <body>. By default it renders inline as
              the last child of Card.Content, which makes the final step's StepSection no longer
              `:last-child` — leaving its connector line dangling below the last step number. */}
          <DndContext
            accessibility={{ container: document.body }}
            onDragEnd={handleDragEnd}
            sensors={sensors}
          >
            <SortableContext items={stepIds} strategy={verticalListSortingStrategy}>
              {steps.map((step, index) => (
                <DripStepCard
                  campaign={campaign}
                  defaultOpen={index + 1 === firstInvalidCardIndex || index + 1 === editingStep}
                  disabled={disabled}
                  editingStep={editingStep}
                  index={index}
                  isComplete={!invalidCardIndexes.includes(index + 1) && editingStep !== index + 1}
                  isReordering={isReordering}
                  key={step.step_id}
                  lock={locks[index]}
                  onChange={applyStepPatch}
                  onDelete={handleDelete}
                  onDone={handleStopEditing}
                  onEdit={handleEdit}
                  onRename={handleRename}
                  step={step}
                  validationErrors={stepErrors[index + 1] ?? {}}
                />
              ))}
            </SortableContext>
          </DndContext>
        </Card.Content>
        <Card.Actions>
          <ButtonValid
            disabled={
              disabled ||
              campaign.isPublished ||
              (!isReordering && (editingStep !== null || steps.length < 2)) ||
              undefined
            }
            errors={reorderStepErrors}
            name={'reorder-steps'}
            onClick={(): void => setIsReordering((value) => !value)}
            tooltipPosition={'top'}
            type={'secondary'}
          >
            {isReordering ? t('Done reordering') : t('Reorder steps')}
          </ButtonValid>
          <ButtonValid
            disabled={
              disabled ||
              campaign.isPublished ||
              editingStep !== null ||
              isReordering ||
              steps.length >= MAX_EMBEDDED_STEPS ||
              undefined
            }
            errors={addStepErrors}
            name={'add-step'}
            onClick={handleAdd}
            tooltipPosition={'top'}
            type={'secondary'}
          >
            {t('Add step')}
          </ButtonValid>
        </Card.Actions>
      </Card>
    </Layout>
  );
}

export default observer(MailrailCampaignStepDripBuilder);
